import { apiRequest } from '../../../api/client';
import type {
  CreateMachineInput,
  CreateMachineResultDto,
  MachineCreateResponseDto,
  MachineLifecycleResponseDto,
  MachineListResponseDto,
  MachineStatusDto,
  MachineSummary,
} from '../types/machine';
import { mapMachineSummary } from '../types/machine';

export interface MachineListResult {
  machines: MachineSummary[];
  nextCursor: string | null;
}

export interface MachineLifecycleResult {
  machine: MachineSummary;
  previousStatus: MachineStatusDto;
  changedAt: string;
}

export async function listMachines(input: {
  search?: string;
  limit?: number;
  cursor?: string | null;
  status?: MachineStatusDto;
} = {}): Promise<MachineListResult> {
  const params = new URLSearchParams();
  if (input.search?.trim()) params.set('search', input.search.trim());
  if (input.limit) params.set('limit', String(input.limit));
  if (input.cursor) params.set('cursor', input.cursor);
  if (input.status && input.status !== 'ACTIVE') params.set('status', input.status);
  const query = params.size ? `?${params.toString()}` : '';
  const response = await apiRequest<MachineListResponseDto>(`/v1/machines${query}`);
  return {
    machines: response.machines.map(mapMachineSummary),
    nextCursor: response.next_cursor,
  };
}

export async function createMachine(
  input: CreateMachineInput,
  idempotencyKey: string,
): Promise<CreateMachineResultDto> {
  return apiRequest<CreateMachineResultDto>('/v1/machines', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify({
      facility_id: input.facilityId,
      machine_name: input.machineName,
      machine_type: input.machineType,
      asset_id: input.assetId,
      manufacturer: input.manufacturer,
      model: input.model,
      location: input.location,
      production_area: input.productionArea,
      production_line: input.productionLine,
      aliases: input.aliases,
      duplicate_decision: input.duplicateDecision ?? null,
    }),
  });
}

export async function selectMachine(machineId: string): Promise<MachineCreateResponseDto> {
  return apiRequest<MachineCreateResponseDto>(`/v1/machines/${encodeURIComponent(machineId)}/select`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

async function changeMachineLifecycle(machineId: string, action: 'archive' | 'restore'): Promise<MachineLifecycleResult> {
  const response = await apiRequest<MachineLifecycleResponseDto>(`/v1/machines/${encodeURIComponent(machineId)}/${action}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return {
    machine: mapMachineSummary(response.machine),
    previousStatus: response.lifecycle.previous_status,
    changedAt: response.lifecycle.changed_at,
  };
}

export function archiveMachine(machineId: string): Promise<MachineLifecycleResult> {
  return changeMachineLifecycle(machineId, 'archive');
}

export function restoreMachine(machineId: string): Promise<MachineLifecycleResult> {
  return changeMachineLifecycle(machineId, 'restore');
}
